import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import YouTubeEmbed from '../components/YouTubeEmbed';
import { listPublicPosts } from '../lib/firestore';
import { featuredDoctors } from '../data/doctors';
import Seo from '../components/Seo';
import { truncateText } from '../lib/seo';

export default function DoctorDetailPage() {
  const { doctorKey } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    async function run() {
      const posts = await listPublicPosts();
      const found = featuredDoctors.find((row) => row.slug === doctorKey || row.id === doctorKey);
      setDoctor(found || null);
      if (!found) {
        setRelatedPosts([]);
        return;
      }
      const keys = [doctorKey, found.id, found.slug].filter(Boolean);
      const filtered = posts.filter((post) => (post.doctorTagIds || []).some((tagId) => keys.includes(tagId)));
      setRelatedPosts(filtered);
    }
    run();
  }, [doctorKey]);

  if (!doctor) return <p>ドクター情報を読み込み中...</p>;

  const imageUrl = doctor.profileImageUrl || doctor.photoUrl;
  const paragraphs = Array.isArray(doctor.profileLong)
    ? doctor.profileLong
    : String(doctor.message || doctor.profile || '').split('\n').filter(Boolean);
  const clinicParagraphs = Array.isArray(doctor.clinicDescription)
    ? doctor.clinicDescription
    : String(doctor.clinicDescription || '').split('\n').filter(Boolean);

  return (
    <>
      <Seo
        title={doctor.name}
        description={truncateText(doctor.message || doctor.profile || doctor.profileLong?.[0] || `${doctor.name} の紹介ページです。`)}
        path={`/doctors/${doctor.slug || doctor.id}`}
        image={imageUrl}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ProfilePage',
          mainEntity: {
            '@type': 'Person',
            name: doctor.name,
            jobTitle: doctor.title || doctor.specialty || '医師',
            worksFor: doctor.clinic ? { '@type': 'MedicalClinic', name: doctor.clinic } : undefined,
            description: truncateText(doctor.message || doctor.profile || doctor.profileLong?.[0] || ''),
          },
        }}
      />
      <article className="detail-layout">
        <section className="panel">
          <Link to="/doctors" className="detail-back-link">← ドクター一覧へ戻る</Link>
          <h1>{doctor.name}</h1>
          <p className="page-lead">{doctor.title || doctor.specialty || 'Drs. Farm ドクター'}</p>
          {doctor.clinic ? (
            <p className="meta-text">
              所属: {doctor.clinicUrl ? <a href={doctor.clinicUrl} target="_blank" rel="noreferrer">{doctor.clinic}</a> : doctor.clinic}
            </p>
          ) : null}
          {doctor.youtubeUrl ? (
            <>
              <YouTubeEmbed url={doctor.youtubeUrl} title={`${doctor.name} のYouTube動画`} />
              <p className="meta-text">
                <a href={doctor.youtubeUrl} target="_blank" rel="noreferrer">YouTubeで開く</a>
              </p>
            </>
          ) : imageUrl ? <img className="doctor-detail-media" src={imageUrl} alt={doctor.name} loading="lazy" /> : null}
        </section>

        <section className="panel">
          <div className="doctor-intro-grid">
            <div>
              <h2>先生のご紹介</h2>
              {paragraphs.map((text, index) => (
                <p key={`doctor-profile-${index}`}>{text}</p>
              ))}
            </div>
            <div>
              <h2>クリニックのご紹介</h2>
              {doctor.clinic ? (
                <>
                  <p>
                    {doctor.clinicUrl ? <a href={doctor.clinicUrl} target="_blank" rel="noreferrer">{doctor.clinic}</a> : doctor.clinic}
                  </p>
                  {clinicParagraphs.map((text, index) => (
                    <p key={`doctor-clinic-${index}`}>{text}</p>
                  ))}
                </>
              ) : (
                <p>クリニック情報は準備中です。</p>
              )}
            </div>
          </div>
        </section>

        <section className="panel">
          <h2>関連記事</h2>
          {relatedPosts.length === 0 ? <p>関連記事はまだありません。</p> : (
            <ul className="timeline">
              {relatedPosts.map((post) => (
                <li key={post.id}>
                  <strong>{post.title}</strong>
                  {post.tags?.length ? <p className="meta-text">タグ: {post.tags.join(' / ')}</p> : null}
                  <Link className="text-btn" to={`/posts/${post.slug}`}>記事を読む</Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </article>
    </>
  );
}
